import BugReportRoundedIcon from "@mui/icons-material/BugReportRounded";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import { Box, Divider, Typography } from "@mui/material";
import pluralize from "pluralize";
import { useContext } from "react";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import CustomToolTip from "../../../../../components/CustomTooltip";
import { EnvFeatureFlagContext } from "../../../../../contexts/EnvFeaturesContext";
import SvgToIcon from "../../../../../icons/SvgToIcon";
import { QueryParam } from "../../../../../utils/constants";
import OverviewGauge from "../../../../stack/environment/components/EntityTypeGauge/OverviewGauge";
import { detectionIcons } from "../../../../stack/environment/icons/iconExport";
import DetectionAlertScenarios from "../../../detections/components/DetectionAlertScenarios";
import CriticalAssetsTooltipContent from "./CriticalAssetsTooltipContent";

export default function ScenarioHeader({
  data,
  goToInvestigation,
  alertActiveDetection,
}) {
  const colors = useColors();
  const { scenariosDemoMode } = useContext(EnvFeatureFlagContext);
  const { trackEvent } = useAPI();

  const scenariosInfo = [
    { quantity: data.total_scenarios, title: "Attack Scenarios" },
    {
      quantity: data.total_paths,
      title: "Attack Path",
      shouldAdjustTitle: true,
    },
    {
      quantity: data.assets_at_risk,
      title: "Critical Assets at Risk",
      shouldAdjustTitle: false,
      hasPopover: true,
    },
  ];

  const riskInfo = [
    // Filter out the risk info if the feature flag is enabled
    scenariosDemoMode && {
      quantity: 18,
      title: "Detected Attack Steps",
      shouldAdjustTitle: false,
      icon: detectionIcons.DetectionRed,
      iconWidth: "30px",
    },
    scenariosDemoMode && {
      quantity: 37,
      title: "CVE's Exploited in-the-wild",
      shouldAdjustTitle: false,
      icon: BugReportRoundedIcon,
      iconWidth: "30px",
    },
  ].filter(Boolean);

  const adjustTitle = (item) => {
    if (item.quantity === 1) return item.title;
    if (item?.pluralize) return item.pluralize;
    return pluralize(item.title, item.quantity);
  };

  const handleAssetChipClick = (asset_id) => {
    const url = `/inventory/assets?${QueryParam.id}=${asset_id}`;
    trackEvent(
      `user clicked on asset chip ${asset_id} in attack surface header critical assets popover`
    );
    window.open(url, "_blank");
  };

  const handleViewAllAssets = () => {
    trackEvent(
      "user clicked view all critical assets from attack surface header"
    );
    window.open("/inventory/assets", "_blank");
  };

  const assetsAtRiskList = data.assets_at_risk_list || [];

  return (
    <Box
      width="100%"
      padding="16px"
      display="flex"
      minWidth="max-content"
      flexDirection="row"
      gap={2}
    >
      {alertActiveDetection === false && scenariosDemoMode && (
        <Box sx={{ width: "30%" }}>
          <DetectionAlertScenarios goToScenarios={goToInvestigation} />
        </Box>
      )}
      <Box height="75px" display="flex" gap={4}>
        {/* Coverage */}
        <Box minWidth="131px" paddingRight={2} position="relative">
          <Box display="flex" justifyContent="flex-end">
            <Typography variant="subtitle1">Coverage</Typography>
          </Box>
          <Box position="relative" left="38px" bottom="5px">
            <OverviewGauge value={data.coverage} />
            <CustomToolTip
              title={
                "Coverage represents the percentage of attack paths mitigated out of the total attack paths, based on the selected critical assets, threats, and risk parameters."
              }
            >
              <HelpOutlineOutlinedIcon
                sx={{
                  color: colors.text_secondary,
                  width: "16px",
                  height: "16px",
                  cursor: "pointer",
                  position: "absolute",
                  top: -17,
                  right: 14,
                }}
              />
            </CustomToolTip>
          </Box>
        </Box>
        {/* Scenarios Info */}
        <Divider orientation="vertical" sx={{ borderColor: colors.divider }} />
        <Box display="flex" gap={4} alignItems="center">
          {scenariosInfo.map((item) => (
            <CustomToolTip
              key={item.title}
              title={
                item.hasPopover ? (
                  <CriticalAssetsTooltipContent
                    assetsAtRiskList={assetsAtRiskList}
                    onAssetClick={handleAssetChipClick}
                    onViewAllClick={handleViewAllAssets}
                  />
                ) : (
                  item.tooltipTitle
                )
              }
              placement="bottom-start"
              disableInteractive={!item.hasPopover}
              enterDelay={200}
              leaveDelay={item.hasPopover ? 200 : 0}
              style={
                item.hasPopover && {
                  borderRadius: "12px",
                  padding: "16px",
                  minWidth: "240px",
                  maxWidth: "480px",
                }
              }
            >
              <Box
                display="flex"
                flexDirection="column"
                gap={0.5}
                sx={{ cursor: item.hasPopover ? "pointer" : "default" }}
              >
                <Typography variant="h6">{item.quantity}</Typography>
                <Typography variant="body2" color={colors.text_primary} noWrap>
                  {item.shouldAdjustTitle ? adjustTitle(item) : item.title}
                </Typography>
              </Box>
            </CustomToolTip>
          ))}
        </Box>
        {/* Risk Info */}
        {riskInfo.length > 0 && (
          <Divider
            orientation="vertical"
            sx={{ borderColor: colors.divider }}
          />
        )}
        <Box display="flex" gap={4} alignItems="center">
          {riskInfo.map((item) => (
            <Box key={item.title} display="flex" gap={2}>
              <Box display="flex" alignItems="center">
                <SvgToIcon
                  IconLogo={item.icon}
                  width={item.iconWidth}
                  style={{ color: colors.error_main }}
                />
              </Box>
              <Box display="flex" flexDirection="column" gap={0.5} paddingY={1}>
                <Typography variant="h6">{item.quantity}</Typography>
                <Typography variant="body2" color={colors.text_primary} noWrap>
                  {adjustTitle(item)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
